import React, { useState, useContext, useCallback } from 'react';
import '../css/employeeLoyalty.css';
import { LocalizationContext } from '../localization/LocalizationContext';
import QrScanner from '../components/QrScanner';
import { lookupCustomer, adjustPoints, newIdempotencyKey } from '../services/employeeLoyaltyApi';

const BRAND_LABELS = {
    nadimi: 'Nadimi',
    copacabana: 'Copacabana',
    aromiitaliani: 'Aromi Italiani',
    poshnosh: 'Posh Nosh',
    symposium: 'Symposium',
    fastdelivery: 'Fast Delivery',
};

const brandLabel = (brandKey) => BRAND_LABELS[brandKey] || brandKey;

const fill = (template, values) =>
    Object.entries(values).reduce((acc, [k, v]) => acc.replace(`{${k}}`, v), template);

const EmployeeLoyalty = () => {
    const { translations } = useContext(LocalizationContext);
    const loc = translations.employeeLoyalty || {};

    const [scanning, setScanning] = useState(false);
    const [manualValue, setManualValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');

    const [lookup, setLookup] = useState(null);
    const [selectedWalletId, setSelectedWalletId] = useState('');
    const [amount, setAmount] = useState('');
    const [employeeName, setEmployeeName] = useState(localStorage.getItem('employeeName') || '');
    const [result, setResult] = useState(null);

    const setError = (text) => {
        setMessage(text);
        setMessageType('error');
    };

    const resetAll = () => {
        setScanning(false);
        setManualValue('');
        setLoading(false);
        setMessage('');
        setMessageType('');
        setLookup(null);
        setSelectedWalletId('');
        setAmount('');
        setResult(null);
    };

    const doLookup = useCallback(async (query) => {
        const q = String(query || '').trim();
        if (!q) return;

        setScanning(false);
        setLoading(true);
        setMessage('');
        setResult(null);

        try {
            const data = await lookupCustomer(q);
            const wallets = data.wallets || [];
            if (wallets.length === 0) {
                setLookup(null);
                setError(loc.noWallet || 'No loyalty wallet available for this customer');
                return;
            }
            setLookup(data);

            const lastBrand = localStorage.getItem('employeeLoyaltyLastBrand');
            const preferred = wallets.find((w) => w.brandKey === lastBrand) || wallets[0];
            setSelectedWalletId(preferred.walletId);
        } catch (err) {
            if (err.code === 'unauthorized') return;
            setLookup(null);
            if (err.code === 'customer_not_found') {
                setError(loc.customerNotFound || 'Customer not found in loyalty system');
            } else {
                setError(err.message || loc.errorMessage || 'Something went wrong. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }, [loc]);

    const handleScan = useCallback((text) => {
        doLookup(text);
    }, [doLookup]);

    const handleCameraError = useCallback(() => {
        setScanning(false);
        setError(loc.cameraError || 'Camera unavailable. Enter the card number manually.');
    }, [loc]);

    const handleManualSubmit = (e) => {
        e.preventDefault();
        doLookup(manualValue);
    };

    const selectedWallet = lookup?.wallets?.find((w) => w.walletId === selectedWalletId) || null;

    const submitAdjust = async (direction) => {
        if (!lookup || !selectedWallet) return;

        const parsed = parseInt(amount, 10);
        if (isNaN(parsed) || parsed <= 0) {
            setError(loc.invalidAmount || 'Please enter a valid amount');
            return;
        }

        const delta = direction === 'reduce' ? -parsed : parsed;

        if (direction === 'reduce' && parsed > selectedWallet.balance) {
            setError(fill(loc.insufficientPoints || 'Insufficient points. Available: {balance}', { balance: selectedWallet.balance }));
            return;
        }

        setLoading(true);
        setMessage('');

        const cardNumber = lookup.customer.cardNumber;
        const brandKey = selectedWallet.brandKey;
        localStorage.setItem('employeeLoyaltyLastBrand', brandKey);
        if (employeeName) localStorage.setItem('employeeName', employeeName);

        try {
            const res = await adjustPoints({
                cardNumber,
                brandKey,
                walletId: selectedWallet.walletId,
                delta,
                employeeName,
                idempotencyKey: newIdempotencyKey(),
            });
            setResult({
                direction,
                points: parsed,
                brandKey,
                cardNumber,
                balanceAfter: res.balanceAfter,
            });
        } catch (err) {
            if (err.code === 'unauthorized') return;
            if (err.code === 'insufficient_balance') {
                setError(fill(loc.insufficientPoints || 'Insufficient points. Available: {balance}', { balance: err.body?.balance ?? selectedWallet.balance }));
            } else if (err.code === 'syrve_error') {
                setError(loc.syrveError || 'Loyalty system error. Please try again.');
            } else {
                setError(err.message || loc.errorMessage || 'Something went wrong. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // ---- Success screen ----
    if (result) {
        const successTemplate = result.direction === 'reduce'
            ? (loc.reduceSuccessMessage || 'Deducted {points} points from card {card}')
            : (loc.successMessage || 'Successfully added {points} points to card {card}');
        return (
            <div className="employee-loyalty-container">
                <div className="employee-loyalty-wrapper">
                    <div className="employee-loyalty-success-panel">
                        <div className="employee-loyalty-success-icon">✓</div>
                        <p className="message success">
                            {fill(successTemplate, { points: result.points, card: result.cardNumber })}
                        </p>
                        <div className="employee-loyalty-balance-after">
                            <span>{loc.currentBalance || 'Current balance'} · {brandLabel(result.brandKey)}</span>
                            <strong>{result.balanceAfter}</strong>
                        </div>
                        <button className="submit-button" onClick={resetAll}>
                            {loc.scanNext || 'Scan next customer'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ---- Customer / adjust screen ----
    if (lookup) {
        return (
            <div className="employee-loyalty-container">
                <div className="employee-loyalty-wrapper">
                    <h1 className="employee-loyalty-title">{loc.title || 'Loyalty Points'}</h1>

                    <div className="employee-loyalty-customer">
                        <div className="employee-loyalty-customer-name">{lookup.customer.name || '—'}</div>
                        {lookup.customer.maskedPhone && (
                            <div className="employee-loyalty-customer-phone">{lookup.customer.maskedPhone}</div>
                        )}
                        <div className="employee-loyalty-customer-card">
                            {loc.cardNumber || 'Card Number'}: {lookup.customer.cardNumber}
                        </div>
                    </div>

                    <div className="employee-loyalty-chips">
                        {lookup.wallets.map((w) => (
                            <button
                                key={w.walletId}
                                type="button"
                                className={`employee-loyalty-chip ${w.walletId === selectedWalletId ? 'is-selected' : ''}`}
                                onClick={() => setSelectedWalletId(w.walletId)}
                            >
                                <span className="chip-brand">{brandLabel(w.brandKey)}</span>
                                <span className="chip-balance">{w.balance}</span>
                            </button>
                        ))}
                    </div>

                    <div className="form-group">
                        <label htmlFor="amount">{loc.amountLabel || 'Points'}</label>
                        <input
                            type="number"
                            id="amount"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder={loc.enterAmount || 'Enter amount'}
                            className="form-input"
                            min="1"
                            step="1"
                            inputMode="numeric"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="employeeName">{loc.employeeName || 'Employee (optional)'}</label>
                        <input
                            type="text"
                            id="employeeName"
                            value={employeeName}
                            onChange={(e) => setEmployeeName(e.target.value)}
                            placeholder={loc.employeeNamePlaceholder || 'Your name'}
                            className="form-input"
                        />
                    </div>

                    {message && <div className={`message ${messageType}`}>{message}</div>}

                    <div className="employee-loyalty-actions">
                        <button
                            type="button"
                            className="employee-loyalty-btn employee-loyalty-btn--add"
                            disabled={loading}
                            onClick={() => submitAdjust('add')}
                        >
                            {loading ? (loc.processing || 'Processing...') : (loc.modeAdd || 'Add Points')}
                        </button>
                        <button
                            type="button"
                            className="employee-loyalty-btn employee-loyalty-btn--reduce"
                            disabled={loading}
                            onClick={() => submitAdjust('reduce')}
                        >
                            {loading ? (loc.processing || 'Processing...') : (loc.modeReduce || 'Reduce Points')}
                        </button>
                    </div>

                    <button type="button" className="employee-loyalty-link" onClick={resetAll}>
                        {loc.scanNext || 'Scan next customer'}
                    </button>
                </div>
            </div>
        );
    }

    // ---- Scan / lookup screen ----
    return (
        <div className="employee-loyalty-container">
            <div className="employee-loyalty-wrapper">
                <h1 className="employee-loyalty-title">{loc.title || 'Loyalty Points'}</h1>

                {scanning ? (
                    <div className="employee-loyalty-scanner">
                        <QrScanner onDecode={handleScan} onError={handleCameraError} />
                        <p className="employee-loyalty-scan-hint">
                            {loc.scanPrompt || "Point the camera at the customer's QR code"}
                        </p>
                        <button type="button" className="employee-loyalty-link" onClick={() => setScanning(false)}>
                            {loc.stopScan || 'Stop Scanning'}
                        </button>
                    </div>
                ) : (
                    <button type="button" className="submit-button" onClick={() => { setMessage(''); setScanning(true); }}>
                        {loc.scanQr || 'Scan QR Code'}
                    </button>
                )}

                <form onSubmit={handleManualSubmit} className="employee-loyalty-manual">
                    <label htmlFor="manual">{loc.manualEntry || 'Or enter card number / phone'}</label>
                    <div className="employee-loyalty-manual-row">
                        <input
                            type="text"
                            id="manual"
                            value={manualValue}
                            onChange={(e) => setManualValue(e.target.value)}
                            placeholder={loc.enterCardNumber || 'Card number or phone'}
                            className="form-input"
                        />
                        <button type="submit" className="employee-loyalty-btn employee-loyalty-btn--add" disabled={loading}>
                            {loading ? (loc.processing || '...') : (loc.lookup || 'Find')}
                        </button>
                    </div>
                </form>

                {message && <div className={`message ${messageType}`}>{message}</div>}
            </div>
        </div>
    );
};

export default EmployeeLoyalty;
